import {useEffect, useState, useRef} from "react";
import {Alert, Button, Input, Space, Table} from "antd";
import {ClipButton, requestFn} from "../helpers";
import {useNavigate} from "react-router";
import {Link} from "react-router-dom";


export const Devices = ({token}) => {
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [data, setData] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: false,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  })
  
  // Track cursors for each page to enable jumping between pages
  const [pageCursors, setPageCursors] = useState(new Map()) // page -> cursor
  const [hasMoreData, setHasMoreData] = useState(true)
  const currentRequestRef = useRef(null) // Track current request for cancellation

  const getTotalCount = async () => {
    // Only get total count for all items (no search filter)
    await requestFn(
      'GET',
      `/devices/count`,
      token,
      (response) => {
        setTotalCount(response.total)
        setPagination(prev => ({
          ...prev,
          total: response.total,
          showQuickJumper: response.total > prev.pageSize
        }))
      },
      (error) => {
        console.log('Error fetching total count:', error)
        setTotalCount(0)
      }
    )
  }

  const getData = async (page = 1, pageSize = pagination.pageSize, searchTerm = search, resetCursors = false) => {
    // Cancel previous request if it exists
    if (currentRequestRef.current) {
      currentRequestRef.current.cancelled = true
    }
    
    // Create new request object
    const currentRequest = { cancelled: false }
    currentRequestRef.current = currentRequest
    
    !loading && setLoading(true)
    
    if (resetCursors) {
      setPageCursors(new Map())
      setHasMoreData(true)
    }
    
    const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''
    
    // Get cursor for the requested page
    let cursor = null
    if (page > 1) {
      cursor = pageCursors.get(page - 1) // Get cursor from previous page
    }
    
    const cursorParam = cursor ? `&cursor=${cursor}` : ''
    
    await requestFn(
      'GET', 
      `/devices?limit=${pageSize}${searchParam}${cursorParam}`, 
      token, 
      (response) => {
        // Only process response if this request hasn't been cancelled
        if (!currentRequest.cancelled) {
          setError('')
          setData(response.data)
          
          // Store cursor for next page if there is more data
          if (response.hasMore && response.nextCursor) {
            setPageCursors(prev => {
              const newMap = new Map(prev)
              newMap.set(page, response.nextCursor)
              return newMap
            })
          }
          
          setHasMoreData(response.hasMore || false)
          
          setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize
          }))
          
          if (searchTerm && response.data.length === 1) {
            setLastSingleResult(response.data[0])
          } else {
            setLastSingleResult(null)
          }
        }
      }, 
      (error) => {
        // Only process error if this request hasn't been cancelled
        if (!currentRequest.cancelled) {
          setError(error)
        }
      }
    )
    
    // Only set loading to false if this request hasn't been cancelled
    if (!currentRequest.cancelled) {
      setLoading(false)
    }
  }

  const [lastSingleResult, setLastSingleResult] = useState(null)
  
  // Single useEffect that handles both initial load and search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.length === 0 || search.length >= 2) {
        getData(1, pagination.pageSize, search, true) // Reset to page 1 and clear cursors
      }
    }, search.length > 0 ? 300 : 0) // No delay for initial load (when search is empty)
    
    return () => clearTimeout(timer)
  }, [search]) // Only depend on search

  // Separate useEffect for initial total count (only once)
  useEffect(() => {
    getTotalCount()
  }, []) // Empty dependency array - only runs once

  const handleSearchChange = (event) => {
    const value = event.target.value
    setSearch(value)
    if (error) {
      setError('')
    }
  }

  const handleTableChange = (paginationInfo) => {
    const { current, pageSize } = paginationInfo
    
    // Don't allow jumping beyond known pages for cursor pagination
    if (current > pagination.current + 1 && !pageCursors.has(current - 1)) {
      return // Prevent jumping too far ahead
    }
    
    getData(current, pageSize, search)
  }

  const getPaginationInfo = () => {
    if (search) {
      // When searching, show current results without total
      return `Showing ${data.length} results`
    }
    
    if (totalCount === 0) return ''
    
    const start = (pagination.current - 1) * pagination.pageSize + 1
    const end = Math.min(start + data.length - 1, totalCount)
    
    return `${start}-${end} of ${totalCount.toLocaleString()} items`
  }

  const columns = [
    {
      title: 'SUID',
      dataIndex: 'suid',
      key: 'suid',
      render: (suid) => (
        <div style={{display: 'flex', justifyContent: 'space-between' }}>
          <Link to={`/device/${suid}`}>{suid}</Link>
          <ClipButton text={suid} />
        </div>
      )
    },
    {
      title: 'MAC',
      dataIndex: 'mac',
      key: 'mac',
      render: (mac) => (
        <div style={{display: 'flex', justifyContent: 'space-between' }}>
          <div> {mac} </div>
          <ClipButton text={mac} />
        </div>
      )
    },
  ];

  return (
    <Space direction='vertical' style={{padding: 30, width: '100%'}}>
      {error && <Alert type='error' message={error}/>}
      Search
      <div style={{display: 'flex', justifyContent: "space-between"}}>
        <div>
          <Space>
            <Input
              value={search}
              onChange={handleSearchChange}
              style={{width: 300}}
              onPressEnter={() => {
                if(lastSingleResult) {
                  navigate('/device/' + lastSingleResult.suid)
                }
              }}
              placeholder="Enter at least 2 characters to search"
            />
            <div style={{ 
              color: '#666', 
              fontSize: '14px',
              fontWeight: 500
            }}>
              {getPaginationInfo()}
            </div>
          </Space>
        </div>
        <Button 
          loading={loading} 
          onClick={() => {
            getData(1, pagination.pageSize, search, true)
          }}
        >
          REFETCH
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        pagination={search ? false : pagination} // Hide pagination when searching
        onChange={handleTableChange}
        loading={loading}
      />
    </Space>
  )
}
